import React, { useState, useCallback, useEffect } from 'react';
import { Grid, TextField, Typography, CircularProgress, Box, withStyles } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { toastr } from 'react-redux-toastr';
import CoherentApi from '../../../collums-components/api/CoherentApi';
import { settingsStyles } from '../styles';

function CoherentSettingsTab({ clinics, coherentClinicKey, setCoherentClinicKey, terminals, setTerminals }) {
    const [loadingTerminals, setLoadingTerminals] = useState({});
    const [terminalOptions, setTerminalOptions] = useState({});

    // Load terminals when coherentClinicKey changes or for clinics without keys (uses default COHERENT_TOKEN)
    useEffect(() => {
        const loadInitialTerminals = async () => {
            // Load terminals for clinics with configured keys
            for (const config of coherentClinicKey) {
                const clinicIdStr = config.clinicId?.toString();
                // Skip if already loaded
                if (!clinicIdStr || terminalOptions[clinicIdStr]) {
                    continue;
                }
                // Only load if key is configured (masked keys will be handled by loadTerminalsForClinic)
                if (!config.key) {
                    continue;
                }
                setLoadingTerminals(prev => ({ ...prev, [clinicIdStr]: true }));
                try {
                    const fetchedTerminals = await CoherentApi.getTerminals(clinicIdStr, null);
                    setTerminalOptions(prev => ({ ...prev, [clinicIdStr]: fetchedTerminals || [] }));
                } catch (error) {
                    console.error('Error fetching terminals:', error);
                    setTerminalOptions(prev => ({ ...prev, [clinicIdStr]: [] }));
                } finally {
                    setLoadingTerminals(prev => ({ ...prev, [clinicIdStr]: false }));
                }
            }

            // Load terminals for clinics without configured keys (uses default COHERENT_TOKEN)
            for (const clinic of clinics) {
                const clinicIdStr = clinic.id?.toString();
                if (!clinicIdStr || terminalOptions[clinicIdStr]) {
                    continue;
                }
                const hasConfiguredKey = coherentClinicKey.some(c => c.clinicId?.toString() === clinicIdStr && c.key);
                if (hasConfiguredKey) {
                    continue;
                }
                setLoadingTerminals(prev => ({ ...prev, [clinicIdStr]: true }));
                try {
                    const fetchedTerminals = await CoherentApi.getTerminals(clinicIdStr, null);
                    setTerminalOptions(prev => ({ ...prev, [clinicIdStr]: fetchedTerminals || [] }));
                } catch (error) {
                    console.error('Error fetching terminals:', error);
                    setTerminalOptions(prev => ({ ...prev, [clinicIdStr]: [] }));
                } finally {
                    setLoadingTerminals(prev => ({ ...prev, [clinicIdStr]: false }));
                }
            }
        };

        if (clinics.length > 0) {
            loadInitialTerminals();
        }
    }, [coherentClinicKey, clinics]); // eslint-disable-line react-hooks/exhaustive-deps

    const getClinicConfig = useCallback(
        clinicId => {
            const clinicIdStr = clinicId?.toString();
            return (
                coherentClinicKey.find(item => item.clinicId?.toString() === clinicIdStr) || {
                    clinicId: clinicIdStr,
                    key: ''
                }
            );
        },
        [coherentClinicKey]
    );

    const updateClinicConfig = useCallback(
        (clinicId, updates) => {
            const clinicIdStr = clinicId?.toString();
            const existingIndex = coherentClinicKey.findIndex(item => item.clinicId?.toString() === clinicIdStr);
            let newConfig;

            if (existingIndex >= 0) {
                newConfig = [...coherentClinicKey];
                newConfig[existingIndex] = { ...newConfig[existingIndex], ...updates };
            } else {
                newConfig = [...coherentClinicKey, { clinicId: clinicIdStr, key: '', ...updates }];
            }

            setCoherentClinicKey(newConfig);
        },
        [coherentClinicKey, setCoherentClinicKey]
    );

    const handleKeyChange = useCallback(
        (clinicId, value) => {
            updateClinicConfig(clinicId, { key: value });
        },
        [updateClinicConfig]
    );

    const handleKeyBlur = useCallback(async (clinicId, key) => {
        // Skip if key is masked (contains asterisks)
        if (key && key.includes('*')) {
            return;
        }

        const clinicIdStr = clinicId?.toString();
        setLoadingTerminals(prev => ({ ...prev, [clinicIdStr]: true }));

        try {
            // If key is empty, fetch using default COHERENT_TOKEN (pass null for key)
            const fetchedTerminals = await CoherentApi.getTerminals(clinicIdStr, key || null);
            setTerminalOptions(prev => ({ ...prev, [clinicIdStr]: fetchedTerminals || [] }));
        } catch (error) {
            console.error('Error fetching terminals:', error);
            if (key) {
                toastr.error('Failed to fetch terminals for the provided key');
            }
            setTerminalOptions(prev => ({ ...prev, [clinicIdStr]: [] }));
        } finally {
            setLoadingTerminals(prev => ({ ...prev, [clinicIdStr]: false }));
        }
    }, []);

    // Get saved terminal IDs for a clinic
    const getSavedTerminalIds = useCallback(
        clinicId => {
            const clinicIdStr = clinicId?.toString();
            return (terminals || []).filter(t => t.clinicId?.toString() === clinicIdStr).map(t => t.terminalId);
        },
        [terminals]
    );

    // Get full terminal objects for saved terminals
    const getSavedTerminals = useCallback(
        (clinicId, options) => {
            const savedIds = getSavedTerminalIds(clinicId);
            return options.filter(opt => savedIds.includes(opt?.deviceId) || savedIds.includes(opt?.id));
        },
        [getSavedTerminalIds]
    );

    const handleTerminalsChange = useCallback(
        (clinicId, selectedTerminals) => {
            const clinicIdStr = clinicId?.toString();

            // Remove old terminals for this clinic
            const otherTerminals = (terminals || []).filter(t => t.clinicId?.toString() !== clinicIdStr);

            // Add new terminals for this clinic
            const newTerminals = selectedTerminals.map(t => ({
                terminalId: t.deviceId || t.id,
                clinicId: clinicIdStr
            }));

            const result = [...otherTerminals, ...newTerminals];

            setTerminals(result);
        },
        [terminals, setTerminals]
    );

    const loadTerminalsForClinic = useCallback(
        async clinicId => {
            const clinicIdStr = clinicId?.toString();
            const config = getClinicConfig(clinicIdStr);

            // If we already have terminals loaded for this clinic, skip
            if (terminalOptions[clinicIdStr]) {
                return;
            }

            // Fetch terminals if key is masked or empty (uses default COHERENT_TOKEN on backend)
            if (!config.key || config.key.includes('*')) {
                setLoadingTerminals(prev => ({ ...prev, [clinicIdStr]: true }));
                try {
                    const fetchedTerminals = await CoherentApi.getTerminals(clinicIdStr, null);
                    setTerminalOptions(prev => ({ ...prev, [clinicIdStr]: fetchedTerminals || [] }));
                } catch (error) {
                    console.error('Error fetching terminals:', error);
                    setTerminalOptions(prev => ({ ...prev, [clinicIdStr]: [] }));
                } finally {
                    setLoadingTerminals(prev => ({ ...prev, [clinicIdStr]: false }));
                }
            }
        },
        [getClinicConfig, terminalOptions]
    );

    const getOptionSelected = (option, value) => {
        const optionId = option?.deviceId?.toString() || option?.id?.toString();
        const valueId = value?.deviceId?.toString() || value?.id?.toString();
        return optionId && valueId && optionId === valueId;
    };

    return (
        <Grid container direction="column" spacing={3}>
            <Grid item>
                <Typography variant="body2" style={{ marginBottom: 16 }}>
                    Configure Coherent API keys and terminals for each clinic. Leave them empty to use default key and
                    all terminals assigned to that key
                </Typography>
            </Grid>

            {clinics.map(clinic => {
                const clinicIdStr = clinic.id?.toString();
                const config = getClinicConfig(clinicIdStr);
                const fetchedOptions = terminalOptions[clinicIdStr] || [];
                const savedTerminals = getSavedTerminals(clinicIdStr, fetchedOptions);
                const isLoading = loadingTerminals[clinicIdStr] || false;

                return (
                    <Grid item key={clinicIdStr}>
                        <Box
                            style={{
                                padding: 16,
                                border: '1px solid #e0e0e0',
                                borderRadius: 4,
                                marginBottom: 8
                            }}
                        >
                            <Typography variant="subtitle1" style={{ fontWeight: 'bold', marginBottom: 16 }}>
                                {clinic.accountName || clinic.name}
                            </Typography>

                            <div style={{ maxWidth: 400 }}>
                                <TextField
                                    label="Coherent API Key"
                                    variant="outlined"
                                    fullWidth
                                    style={{ marginBottom: 16 }}
                                    value={config.key || ''}
                                    onChange={e => handleKeyChange(clinicIdStr, e.target.value)}
                                    onBlur={e => handleKeyBlur(clinicIdStr, e.target.value)}
                                    placeholder="Enter Coherent API key"
                                    InputProps={{
                                        endAdornment: isLoading ? <CircularProgress size={20} /> : null
                                    }}
                                />

                                <Autocomplete
                                    multiple
                                    style={{ width: '100%' }}
                                    options={fetchedOptions}
                                    getOptionLabel={option => option?.name || option?.deviceId || ''}
                                    getOptionSelected={getOptionSelected}
                                    value={savedTerminals}
                                    onChange={(_, newValue) => handleTerminalsChange(clinicIdStr, newValue)}
                                    onOpen={() => loadTerminalsForClinic(clinicIdStr)}
                                    loading={isLoading}
                                    renderInput={params => (
                                        <TextField
                                            {...params}
                                            label="Terminals"
                                            variant="outlined"
                                            style={{ width: '100%' }}
                                            placeholder={savedTerminals.length ? '' : 'Select terminals'}
                                            InputProps={{
                                                ...params.InputProps,
                                                style: {
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    paddingTop: 1,
                                                    paddingBottom: 11
                                                },
                                                endAdornment: (
                                                    <>
                                                        {isLoading ? (
                                                            <CircularProgress color="inherit" size={20} />
                                                        ) : null}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                )
                                            }}
                                        />
                                    )}
                                />
                            </div>
                        </Box>
                    </Grid>
                );
            })}

            {clinics.length === 0 && (
                <Grid item>
                    <Typography variant="body2" color="textSecondary">
                        No clinics available. Please add clinics first.
                    </Typography>
                </Grid>
            )}
        </Grid>
    );
}

export default withStyles(settingsStyles)(CoherentSettingsTab);
